import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Download, Eye } from "lucide-react";
import api, { API } from "@/lib/api";
import { TOKEN_KEY } from "@/lib/api";
import { rp, num, fmtDate, TYPE_LABELS, monthOptions } from "@/lib/format";
import { PageTitle, Pill } from "@/components/common";
import { SourcePhotoButton } from "@/components/SourcePhotoDialog";
import TrxActions from "@/components/TrxActions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function Ledger() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filters, setFilters] = useState({ search: "", month: "", transaction_type: "", status: "", worker_id: "" });
  const [workers, setWorkers] = useState([]);
  const [detail, setDetail] = useState(null);
  const months = monthOptions(12);

  useEffect(() => { api.get("/workers").then((r) => setWorkers(r.data)); }, []);

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: pageSize });
    Object.entries(filters).forEach(([k, v]) => v && p.append(k, v));
    api.get(`/transactions?${p.toString()}`).then((r) => {
      setItems(r.data.items);
      setTotal(r.data.total);
    });
  }, [page, pageSize, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v === "ALL" ? "" : v })); };

  const doExport = (fmt) => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && k !== "search" && p.append(k, v));
    if (fmt !== "pdf") p.append("fmt", fmt);
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/export/${fmt === "pdf" ? "ledger-pdf" : "ledger"}?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = `ledger.${fmt}`; a.click();
        URL.revokeObjectURL(url);
        toast.success(`Export ${fmt.toUpperCase()} berhasil`);
      })
      .catch(() => toast.error("Export gagal"));
  };

  const pages = Math.ceil(total / pageSize) || 1;

  return (
    <div>
      <PageTitle title="Jurnal Umum (General Ledger)" desc="Seluruh transaksi produksi & keuangan — sumber data resmi."
        right={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => doExport("csv")} data-testid="btn-export-csv"><Download className="w-4 h-4 mr-1.5" />CSV</Button>
            <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="btn-export-excel"><Download className="w-4 h-4 mr-1.5" />Excel</Button>
            <Button variant="outline" onClick={() => doExport("pdf")} data-testid="btn-export-pdf"><Download className="w-4 h-4 mr-1.5" />PDF</Button>
          </div>
        } />

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input className="pl-8" placeholder="Cari TRX / worker / job…" value={filters.search}
              onChange={(e) => setF("search", e.target.value)} data-testid="input-filter-search" />
          </div>
          <Select value={filters.month || "ALL"} onValueChange={(v) => setF("month", v)}>
            <SelectTrigger data-testid="select-filter-month"><SelectValue placeholder="Bulan" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Bulan</SelectItem>
              {months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.transaction_type || "ALL"} onValueChange={(v) => setF("transaction_type", v)}>
            <SelectTrigger data-testid="select-filter-type"><SelectValue placeholder="Tipe" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Tipe</SelectItem>
              {Object.keys(TYPE_LABELS).map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.worker_id || "ALL"} onValueChange={(v) => setF("worker_id", v)}>
            <SelectTrigger data-testid="select-filter-worker"><SelectValue placeholder="Worker" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Worker</SelectItem>
              {workers.map((w) => <SelectItem key={w.worker_id} value={w.worker_id}>{w.worker_id} — {w.worker_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || "ALL"} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger data-testid="select-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Status</SelectItem>
              {["CONFIRMED", "PENDING", "CANCELLED", "CORRECTED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="table-general-ledger">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
                {["Tanggal", "TRX ID", "Worker", "Job", "Tipe", "Produk", "Qty", "Good", "Reject", "Harga", "Jumlah", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.transaction_id} className="border-b border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{fmtDate(t.transaction_date)}</td>
                  <td className="px-3 py-2.5 mono text-xs text-slate-500">{t.transaction_id}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap"><span className="font-semibold text-slate-800">{t.worker_id}</span> <span className="text-slate-500">{t.worker_name}</span></td>
                  <td className="px-3 py-2.5 mono text-xs text-slate-400">{t.job_id || "-"}</td>
                  <td className="px-3 py-2.5"><Pill kind="type" value={t.transaction_type} /></td>
                  <td className="px-3 py-2.5 text-slate-700">{t.product_name || t.bon_item_name || "-"}{t.ref_no && <span className="ml-1.5 text-[10px] mono text-slate-400" data-testid={`ledger-refno-${t.transaction_id}`}>Nomor Potongan {t.ref_no}</span>}</td>
                  <td className="px-3 py-2.5 mono text-right">{t.quantity ? num(t.quantity) : "-"}</td>
                  <td className="px-3 py-2.5 mono text-right text-emerald-700">{t.transaction_type === "SETOR" ? num(t.good_quantity) : "-"}</td>
                  <td className="px-3 py-2.5 mono text-right text-rose-600">{t.reject_quantity ? num(t.reject_quantity) : "-"}{t.repair_quantity > 0 && <span className="block text-[10px] text-orange-700 font-bold">RIJEK {num(t.repair_quantity)}</span>}</td>
                  <td className="px-3 py-2.5 mono text-right text-slate-600">{t.unit_price != null ? rp(t.unit_price) : (t.price_pending ? "PENDING" : "-")}</td>
                  <td className="px-3 py-2.5 mono text-right font-semibold">{t.amount ? rp(t.amount) : "-"}</td>
                  <td className="px-3 py-2.5"><Pill value={t.status} /></td>
                  <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1"><TrxActions t={t} onDone={load} /><button onClick={() => setDetail(t)} className="text-slate-400 hover:text-slate-700" data-testid={`ledger-view-${t.transaction_id}`}><Eye className="w-4 h-4" /></button></span></td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={13} className="px-3 py-10 text-center text-slate-400">Tidak ada transaksi.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
          <div className="text-slate-500">Total <span className="font-semibold text-slate-700">{num(total)}</span> transaksi</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="ledger-prev-page">Sebelumnya</Button>
            <span className="text-slate-600">Hal {page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="ledger-next-page">Berikutnya</Button>
          </div>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-md" data-testid="ledger-detail-modal">
          <DialogHeader><DialogTitle className="mono text-base">{detail?.transaction_id}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              {[
                ["Tanggal", `${fmtDate(detail.transaction_date)} ${detail.transaction_time || ""} WITA`],
                ["Worker", `${detail.worker_id} — ${detail.worker_name} (${detail.worker_type})`],
                ["Job", detail.job_id || "-"],
                ["Tipe", detail.transaction_type],
                ["Produk / Barang", detail.product_name || detail.bon_item_name || "-"],
                ["Qty", detail.quantity ? num(detail.quantity) + " " + (detail.unit || "PCS") : "-"],
                ["Good / Reject", `${num(detail.good_quantity)} / ${num(detail.reject_quantity)}`],
                ["Rijek bisa diperbaiki", detail.repair_quantity ? `${num(detail.repair_quantity)} PCS (PERBAIKAN RIJEK)` : "-"],
                ["Harga Satuan", detail.unit_price != null ? rp(detail.unit_price) : "-"],
                ["Tarif Ongkos", detail.sewing_rate != null ? rp(detail.sewing_rate) : "-"],
                ["Jumlah", detail.amount ? rp(detail.amount) : "-"],
                ["Status", detail.status],
                ["Diinput oleh", detail.entry_by || detail.admin_user || "-"],
                ["Admin", detail.admin_user || "-"],
                ["Catatan", detail.notes || "-"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-1.5">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-medium text-slate-800 text-right">{v}</span>
                </div>
              ))}
              {detail.source_image && (
                <div className="pt-2"><SourcePhotoButton id={detail.transaction_id} label="VIEW SOURCE PHOTO" /></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
